import { type ContextProps, useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger, type Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";
import { Stack } from "@mantine/core";
import { AnalyticsProvider, useReactAnalytics } from "@soluto-private/eventualize-react";
import { type GuideCommand, Timeline } from "@soluto-private/expert-workspace-timeline";
import { useMemo } from "react";
import { env } from "@/config";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { usePilots } from "@/pilot";
import { isChatTask, useActiveTask, useAgentFriendlyName, usePartner, useSessionId, type VoiceTask } from "@/sdk";
import { userCache } from "@/shared-utils";
import { IssueSummary } from "./IssueSummary";
import classes from "./PlatformTimeline.module.css";
import { useSetupSubscriptions } from "./state";
import { features } from "./utils";

const logger = getLogger({
    rootModule: "PlatformTimeline",
    supportTeam: "solve",
});

function SetupSubscriptions({ loggerWithContext }: { loggerWithContext: Logger }) {
    useSetupSubscriptions(loggerWithContext);

    return <></>;
}

export function PlatformTimeline({ guide }: { guide?: GuideCommand }) {
    const task = useActiveTask();
    const isChat = isChatTask(task);
    // TODO(afd): Revisit this to properly handle nonvoice tasks and not unsafely cast this
    const callSid = (task as VoiceTask)?.agentCallId;
    const chatId = isChat ? task.chatId : undefined;
    const expertId = userCache.employeeId ?? "";
    const expertName = useAgentFriendlyName();
    const sessionId = useSessionId();
    const partner = usePartner();
    const {
        features: { isVerizonCareEnabled, isChatIssueSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { ozmo: isOzmoEnabled } = usePartnerFeatureConfigured();
    const { gaiaPayloadProps: partnerConfigGaiaPayloadProps } = usePartnerConfig();

    // TODO: Setting it like this is temporary until we update app config to support separate chat/voice gaia payload configs (EWPM-300)
    const gaiaPayloadProps = useMemo(() => {
        return isChat
            ? ({
                  appName: "expert-assist-messaging-reactive",
                  areExperimentsEnabled: false,
              } as GaiaPayloadConfigs)
            : partnerConfigGaiaPayloadProps;
    }, [isChat, partnerConfigGaiaPayloadProps]);

    const { pilots } = usePilots();

    const lob = pilots.includes("care") && isVerizonCareEnabled ? "care" : undefined;

    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = dispatcher
        .withIdentities({
            ...(expertId && { ExpertId: expertId }),
            ...(callSid && { CallSid: callSid }),
            ...(chatId && { ChatId: chatId }),
        })
        .withExtra({ tool: "expert-assist" });

    const featuresContext = useMemo(() => {
        return {
            appName: "expert-workspace",
            employeeId: expertId,
            environment: env.mode,
            channel: env.channel,
            partner,
            source: "expert-ui",
            lob,
        } as ContextProps;
    }, [expertId, lob, partner]);

    const IssueSummaryComponent = useMemo(() => {
        return (
            <>
                {isChat && isChatIssueSummaryEnabled && (
                    <IssueSummary
                        activeTask={task}
                        logger={logger}
                        isExternal={env.gaiaExternal ?? false}
                        gaiaPayloadProps={gaiaPayloadProps}
                    />
                )}
            </>
        );
    }, [isChat, isChatIssueSummaryEnabled, task, gaiaPayloadProps]);

    return (
        <AnalyticsProvider dispatcher={enhancedDispatcher}>
            <Stack gap="0" pb="sm" className={classes.timeline} miw="100%">
                <Timeline
                    isExternal={env.gaiaExternal ?? false}
                    gaiaPayloadProps={gaiaPayloadProps}
                    callSid={callSid}
                    chatId={chatId}
                    defaultFeatures={features}
                    expertId={expertId}
                    expertName={expertName}
                    featuresContext={featuresContext}
                    toolboxContext={{ guide, isOzmoEnabled }}
                    logger={logger.child({
                        sessionId,
                        callSid,
                        chatId,
                    })}
                    lob={lob}
                    partner={partner}
                    sessionId={sessionId}
                    SubscriptionsWithFeatures={
                        isChat ? (
                            <></>
                        ) : (
                            <SetupSubscriptions
                                loggerWithContext={logger.child({
                                    sessionId,
                                    callSid,
                                })}
                            />
                        )
                    }
                    IssueSummaryComponent={IssueSummaryComponent}
                />
            </Stack>
        </AnalyticsProvider>
    );
}
